import {createRoot, type Root} from "react-dom/client";
import {flushSync} from "react-dom";
import React, {StrictMode} from "react";

/* type alias for a react function compontent with generic props */
export type ControllerComponent<Props> = (props: Props) => React.JSX.Element;
export type CommandMap = Record<string, unknown>;

/* type alias for a function that adds a command listener for a specific command */
export type AddCommandListener<Commands extends CommandMap> = <
	CommandName extends string & keyof Commands,
>(
	command: CommandName,
	/* the handler of command CommandName, has the payload type Commands[CommandName] */
	handler: (data: Commands[CommandName]) => void,
) => () => void;

/* type for the addCommandListener passed as a prop to the controller component */
export type WithCommandListener<Commands extends CommandMap> = {
	addCommandListener: AddCommandListener<Commands>;
};

export class ReactIsland<
	Props extends object,
	Commands extends CommandMap = CommandMap,
> {
	private readonly root: Root;
	private readonly handlers = new Map<string, (data: unknown) => void>();
	private readonly createdContainer: HTMLElement | undefined;

	constructor(
		component: ControllerComponent<Props & WithCommandListener<Commands>>,
		props: Props,
		container?: HTMLElement /* optionally provide a container to render the react app into, if not provided, a default div will be created and appended to the body */,
	) {
		const resolvedContainer =
			container ??
			(() => {
				/* helper to create a default container and append it to the body */
				const el = document.createElement("div");
				document.body.appendChild(el);
				this.createdContainer = el;
				return el;
			})();

		this.root = createRoot(resolvedContainer, {
			identifierPrefix: crypto.randomUUID()
		});

		/* function to register command listeners on the bridge, will be passed to the component as a extra prop */
		const addCommandListener: AddCommandListener<Commands> = <
			CommandName extends string & keyof Commands,
		>(
			command: CommandName,
			handler: (data: Commands[CommandName]) => void,
		) => {
			/* the map erases the per-command payload type; dispatchCommand re-correlates key and payload */
			this.handlers.set(command, handler as (data: unknown) => void);

			/* return a cleanup function that removes the handler when called */
			return () => this.handlers.delete(command);
		};

		/*
		 * Render synchronously so a command dispatched right after construction is not lost:
		 * flushSync drives the commit and both effect phases to completion, so the component's
		 * command listeners are registered before control returns to the caller.
		 *
		 * Alternative was a queue implementation => to much complexity.
		 *
		 * https://react.dev/reference/react-dom/flushSync
		 */
		flushSync(() => {
			this.root.render(
				<StrictMode>
					{React.createElement(component, {
						...props,
						addCommandListener,
					} as Props & WithCommandListener<Commands>)}
				</StrictMode>,
			);
		});
	}

	/*
	Dispatches a command with the given name and data, invoking the registered handler if one exists inside the React app.
	*/
	public dispatchCommand<CommandName extends keyof Commands & string>(
		command: CommandName,
		data: Commands[CommandName],
	) {
		const handler = this.handlers.get(command);

		if (!handler) {
			/* dont throw, but show a warning in the console instead */
			console.warn(`No handler for "${command}"`);
			return;
		}

		/* call the command handler and pass the data to it */
		handler(data);
	}

	/* Destroy function for completeness */
	public destroy() {
		this.root.unmount();
		this.handlers.clear();
		if (this.createdContainer) {
			this.createdContainer.remove();
		}
	}
}
